
import React, { useMemo, useRef, useState } from "react";
import Graph from "react-graph-vis";
import CloseIcon from '@mui/icons-material/Close';
import { Box, Dialog, IconButton, Stack, Typography } from "@mui/material";
import './networkGraph.css'

function NetworkGraph({ id, topology, setDiagram, diagram }) {
  const networkRef = useRef(null);
  const [manualPositions, setManualPositions] = useState({});

  const { nodes, edges } = useMemo(() => {
    const rawNodes = topology?.nodes ?? topology?.data?.nodes ?? [];
    const rawEdges = topology?.edges ?? topology?.data?.edges ?? [];

    const normalizedNodes = Array.isArray(rawNodes)
      ? rawNodes.map((n, idx) => {
        const nodeId = n?.id ?? n?.node_id ?? n?.pk ?? idx + 1;
        const image = n?.image ?? n?.img ?? n?.icon ?? null;
        const shape = image ? (n?.shape ?? "image") : (n?.shape ?? "dot");
        return {
          ...n,
          id: nodeId,
          label: n?.label ?? n?.name ?? String(nodeId),
          title: n?.title ?? n?.label ?? n?.name ?? "",
          ...(manualPositions[nodeId] ?? (n?.x != null && n?.y != null ? { x: n.x, y: n.y } : {})),
          ...(image ? { image } : {}),
          shape,
        };
      })
      : [];

    const normalizedEdges = Array.isArray(rawEdges)
      ? rawEdges
        .map((e) => {
          const from = e?.from ?? e?.source ?? e?.start ?? e?.from_id ?? e?.fromId;
          const to = e?.to ?? e?.target ?? e?.end ?? e?.to_id ?? e?.toId;
          if (from == null || to == null) return null;
          return { ...e, from, to };
        })
        .filter(Boolean)
      : [];

    return { nodes: normalizedNodes, edges: normalizedEdges };
  }, [topology, manualPositions]);

  const graph = useMemo(
    () => ({
      nodes,
      edges,
    }),
    [nodes, edges]
  );

  const options = {
    autoResize: true,
    height: '420px',
    width: '100%',
    nodes: {
      size: 18,
      font: { color: "#ffffff" },
    },

    edges: {
      color: "#909398",
      arrows: { to: { enabled: true, scaleFactor: 0.6 } },
    },
    layout: {
      hierarchical: false,
      improvedLayout: false,
    },
    physics: {
      enabled: false,
    },
    interaction: {
      dragNodes: true,
      dragView: true,
      zoomView: true,
    },

  };


  const events = {
    dragEnd: function (event) {
      const movedNodes = event?.nodes ?? [];
      if (!networkRef.current || movedNodes.length === 0) return;

      const positions = networkRef.current.getPositions(movedNodes);
      setManualPositions((prev) => {
        const next = { ...prev };
        movedNodes.forEach((nodeId) => {
          if (positions[nodeId]) {
            next[nodeId] = {
              x: positions[nodeId].x,
              y: positions[nodeId].y,
            };
          }
        });
        return next;
      });
    },
    select: function (event) {
      var { nodes, edges } = event;
    },

  };
  const hasData = (nodes?.length ?? 0) > 0;

  return (
    <Dialog open={Boolean(diagram)} onClose={() => setDiagram(false)} maxWidth="md" fullWidth>
      <Box sx={{ bgcolor: "#161C24" }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" px={2} py={1.5}>
          <Typography variant="h2" sx={{ color: "rgba(255,255,255,0.87)" }}>
            Network Topology
          </Typography>
          <IconButton onClick={() => setDiagram(false)} aria-label="Close topology dialog">
            <CloseIcon sx={{ color: "rgba(255,255,255,0.7)" }} />
          </IconButton>
        </Stack>

        <Box sx={{ px: 2, pb: 2 }}>
          {!hasData ? (
            <Box
              sx={{
                borderRadius: 2,
                border: "1px solid rgba(255,255,255,0.12)",
                p: 3,
                color: "rgba(255,255,255,0.7)",
              }}
            >
              <Typography variant="body1">
                Topology is not available for this scenario yet.
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                borderRadius: 2,
                overflow: "hidden",
                border: "1px solid rgba(255,255,255,0.12)",
                bgcolor: "#0B1220",
              }}
            >
              <Graph
                graph={graph}
                options={options}
                events={events}
                getNetwork={(network) => {
                  networkRef.current = network;
                }}
              />
            </Box>
          )}
        </Box>
      </Box>
    </Dialog>
  );
}

export default NetworkGraph;